import { useCallback, useEffect, useState } from "react";
import { FaBalanceScaleLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioUnidadeMedida } from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Cadastro de Unidades de Medida",
  descricao:
    "Unidade de Medida define como a quantidade de um item é contada (kg, litro, unidade, etc.). Todo Item de Estoque precisa apontar para uma unidade cadastrada aqui — por isso vale cadastrar as unidades antes de criar os itens.",
  secoes: [
    {
      titulo: "Dados da Unidade",
      campos: [
        {
          nome: "Sigla",
          obrigatorio: true,
          explicacao:
            "Abreviação usada nas telas e relatórios (ex.: kg, L, un). Máximo de 5 caracteres — é salva automaticamente em maiúsculo.",
        },
        {
          nome: "Descrição",
          obrigatorio: true,
          explicacao:
            "Nome completo da unidade (ex.: Quilograma, Litro, Unidade), para deixar claro o que a sigla representa.",
        },
      ],
    },
  ],
  dicas: [
    "No cadastro, você pode clicar em uma das 'Unidades Comuns' sugeridas para preencher sigla e descrição automaticamente, sem digitar.",
    "Uma unidade com itens de estoque vinculados não pode ser excluída — o botão Excluir fica desabilitado nesse caso.",
  ],
};

export default function GestaoUnidadesMedida() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [unidades, setUnidades] = useState([]);
  const [unidadesFiltradas, setUnidadesFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    sigla: "",
    descricao: "",
  });

  // Unidades de medida comuns para exemplo
  const UNIDADES_COMUNS = [
    { sigla: "kg", descricao: "Quilograma" },
    { sigla: "g", descricao: "Grama" },
    { sigla: "L", descricao: "Litro" },
    { sigla: "mL", descricao: "Mililitro" },
    { sigla: "un", descricao: "Unidade" },
    { sigla: "cx", descricao: "Caixa" },
    { sigla: "pc", descricao: "Pacote" },
    { sigla: "m", descricao: "Metro" },
    { sigla: "m²", descricao: "Metro quadrado" },
    { sigla: "m³", descricao: "Metro cúbico" },
    { sigla: "sc", descricao: "Saca" },
    { sigla: "ton", descricao: "Tonelada" },
  ];

  // Carregar unidades de medida
  const carregarUnidades = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioUnidadeMedida.listar();
      const dados = response.data.results || response.data;
      setUnidades(dados);
      setUnidadesFiltradas(dados);
    } catch (error) {
      console.error("Erro ao carregar unidades de medida:", error);
      alert("Erro ao carregar lista de unidades de medida");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarUnidades();
  }, [carregarUnidades]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.sigla.trim()) {
      alert("Preencha a sigla da unidade de medida.");
      return false;
    }
    if (!formData.descricao.trim()) {
      alert("Preencha a descrição da unidade de medida.");
      return false;
    }
    // Verificar se sigla tem no máximo 5 caracteres
    if (formData.sigla.length > 5) {
      alert("A sigla deve ter no máximo 5 caracteres.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      sigla: formData.sigla.toUpperCase(),
      descricao: formData.descricao,
    };

    try {
      if (editando) {
        const response = await repositorioUnidadeMedida.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Unidade de medida atualizada com sucesso!");
          handleCancel();
          carregarUnidades();
        }
      } else {
        const response = await repositorioUnidadeMedida.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Unidade de medida cadastrada com sucesso!");
          handleCancel();
          carregarUnidades();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (unidade) => {
    setFormData({
      sigla: unidade.sigla || "",
      descricao: unidade.descricao || "",
    });
    setEditando(true);
    setEditandoId(unidade.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaUnidade = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      sigla: "",
      descricao: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta unidade de medida? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioUnidadeMedida.excluir(id);
      alert("Unidade de medida excluída com sucesso!");
      carregarUnidades();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir unidade de medida. Verifique se não há produtos vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...unidades];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(u => 
        u.sigla?.toLowerCase().includes(buscaLower) ||
        u.descricao?.toLowerCase().includes(buscaLower)
      );
    }
    
    setUnidadesFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setUnidadesFiltradas(unidades);
  };

  // Sugerir unidade comum
  const sugerirUnidade = (unidade) => {
    setFormData({
      sigla: unidade.sigla,
      descricao: unidade.descricao,
    });
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Unidades de Medida <FaBalanceScaleLeft size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaUnidade}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Unidade
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Unidade de Medida" : "Cadastrar Nova Unidade de Medida"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Sigla e Descrição */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Sigla *</label>
                    <input
                      type="text"
                      name="sigla"
                      value={formData.sigla}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: kg, L, m, un"
                      maxLength={5}
                      required
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Máximo de 5 caracteres. Será salvo em maiúsculo.
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                    <input
                      type="text"
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Quilograma, Litro, Metro, Unidade"
                      required
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Nome completo da unidade de medida
                    </span>
                  </div>
                </div>

                {/* Sugestões de unidades comuns */}
                {!editando && (
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-2">Unidades Comuns (clique para usar)</label>
                    <div className="flex flex-wrap gap-2">
                      {UNIDADES_COMUNS.map((unidade, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => sugerirUnidade(unidade)}
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-green-100 border border-gray-300 rounded-full transition-colors"
                        >
                          <span className="font-mono font-medium">{unidade.sigla}</span>
                          <span className="text-gray-500 ml-1">-</span>
                          <span className="text-gray-600 ml-1">{unidade.descricao}</span>
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500 mt-2">
                      Clique em uma unidade para preencher o formulário automaticamente
                    </span>
                  </div>
                )}

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Sigla ou Descrição</label>
                  <input
                    type="text"
                    placeholder="Digite a sigla ou descrição..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Unidades de Medida */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {unidadesFiltradas.length} unidade(s) de medida encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Sigla</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Qtd. Produtos</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unidadesFiltradas.map((unidade) => (
                        <tr key={unidade.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{unidade.id}</td>
                          <td className="p-3 text-sm">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-mono font-bold">
                              {unidade.sigla}
                            </span>
                           </td>
                          <td className="p-3 text-sm">{unidade.descricao}</td>
                          <td className="p-3 text-sm text-center">
                            <span className="font-medium">{unidade.produtos_count || 0}</span>
                            <span className="text-gray-500 text-xs ml-1">produtos</span>
                           </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(unidade)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(unidade.id)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  unidade.produtos_count > 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                                title={unidade.produtos_count > 0 ? "Unidade com produtos não pode ser excluída" : "Excluir"}
                                disabled={unidade.produtos_count > 0}
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      ))}
                      
                      {unidadesFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhuma unidade de medida encontrada com os filtros selecionados."
                              : "Nenhuma unidade de medida cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}